import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Autocomplete,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Business as ProjectIcon,
  Apartment as BuildingIcon,
  Layers as FloorIcon,
  Home as FlatIcon,
  CurrencyRupee as RupeeIcon,
} from '@mui/icons-material';
import { getProjects, getPlotsByProject, searchFloorData, createFlat, updateFlat, searchFlats } from '../../../services/project';
import { globalMasterService } from '../../../services/stateAPI';
import { showSnackbar } from '../../../components/Snackbar';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const FlatForm = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const flatDataFromState = location.state?.flat;
  const projectDataFromState = location.state?.project;
  const buildingDataFromState = location.state?.building;
  const floorDataFromState = location.state?.floor;
  const typeDataFromState = location.state?.type;
  
  const getInitialId = (data) => {
    if (!data || data === 'all') return '';
    if (typeof data === 'string') return data;
    return data._id || data.id || '';
  };

  const isEditMode = !!(id || flatDataFromState);

  const urlParams = new URLSearchParams(location.search);
  const urlProjectId = urlParams.get('projectId');
  const urlBuildingId = urlParams.get('buildingId');
  const urlFloorId = urlParams.get('floorId');

  const normalizeStatus = (s) => {
    if (!s) return 'Available';
    const l = s.toLowerCase();
    if (l === 'available') return 'Available';
    if (l === 'booked') return 'Booked';
    if (l === 'underconstruction') return 'underConstruction';
    if (l === 'hold') return 'Hold';
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const initialProjectId = flatDataFromState?.projectId || 
                           flatDataFromState?.project_id || 
                           getInitialId(projectDataFromState) || 
                           (typeof buildingDataFromState === 'object' ? buildingDataFromState?.projectId : '') || 
                           urlProjectId || '';

  const initialBuildingId = flatDataFromState?.buildingId || 
                            flatDataFromState?.building_id ||
                            getInitialId(buildingDataFromState) || 
                            urlBuildingId || '';

  const initialFloorId = flatDataFromState?.floorId || 
                         flatDataFromState?.floor_id ||
                         getInitialId(flatDataFromState?.floor) ||
                         getInitialId(floorDataFromState) || 
                         urlFloorId || '';

  const isPreSelectedProject = !!initialProjectId;
  const isPreSelectedBuilding = !!initialBuildingId;
  const isPreSelectedFloor = !!initialFloorId;

  const [projects, setProjects] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [loadingFloors, setLoadingFloors] = useState(false);
  const [error, setError] = useState('');

  const [flatTypeOptions, setFlatTypeOptions] = useState([]);
  const [facingOptions, setFacingOptions] = useState([]);

  const [formData, setFormData] = useState({
    id: id || flatDataFromState?._id || flatDataFromState?.id || '',
    projectId: initialProjectId,
    projectType: flatDataFromState?.projectType || typeDataFromState || (typeof projectDataFromState === 'object' ? projectDataFromState?.projectType : '') || 'Apartment',
    buildingId: initialBuildingId,
    floorId: initialFloorId,
    number: flatDataFromState?.number || flatDataFromState?.flatNumber || '',
    type: flatDataFromState?.type || flatDataFromState?.flatType || '',
    facing: flatDataFromState?.facing || '',
    inventoryType: flatDataFromState?.inventoryType || 'Apartment',
    status: normalizeStatus(flatDataFromState?.status || 'Available'),
    remark: flatDataFromState?.remark || '',

    // Financials
    totalSize: flatDataFromState?.totalSize || '',
    price: flatDataFromState?.price ? (flatDataFromState.price / 100000).toString() : '',
    plcPercent: flatDataFromState?.plcPercent || flatDataFromState?.plc || '0',
    companyDiscountPercent: flatDataFromState?.companyDiscountPercent || '0',
    discountPerSqFt: flatDataFromState?.discountPerSqFt || '0',
    discountAmount: flatDataFromState?.discountAmount || '0',
    costLacs: flatDataFromState?.costLacs || '',
    guidelineValue: flatDataFromState?.guidelineValue || '',
  });

  const fetchFlatById = async (flatId) => {
    try {
      setLoading(true);
      const response = await searchFlats({ id: flatId });
      const data = Array.isArray(response) ? response[0] : (response.data?.[0] || response[0]);
      
      if (data) {
        setFormData(prev => ({
          ...prev,
          id: data._id || data.id,
          projectId: getInitialId(data.projectId || data.project_id || data.project),
          projectType: data.inventoryType || data.projectType || data.type || 'Apartment',
          buildingId: getInitialId(data.buildingId || data.building_id || data.building),
          floorId: getInitialId(data.floorId || data.floor_id || data.floor),
          number: data.flatNumber || data.number || '',
          type: data.flatType || data.type || '',
          facing: data.facing || '',
          inventoryType: data.inventoryType || 'Apartment',
          status: normalizeStatus(data.status),
          remark: data.remark || '',
          totalSize: data.totalSize || '',
          price: data.price ? (data.price / 100000).toString() : '',
          plcPercent: data.plcPercent || data.plc || '0',
          companyDiscountPercent: data.companyDiscountPercent || '0',
          discountPerSqFt: data.discountPerSqFt || '0',
          discountAmount: data.discountAmount || '0',
          costLacs: data.cost ? (data.cost / 100000).toFixed(2) : '',
          guidelineValue: data.guidelineValue || '',
        }));
        
        const pId = getInitialId(data.projectId || data.project_id || data.project);
        const bId = getInitialId(data.buildingId || data.building_id || data.building);
        if (pId) fetchBuildings(pId);
        if (bId) fetchFloors(bId);
        if (data.projectType) fetchProjects(data.projectType);
      }
    } catch (err) {
      console.error('Error fetching flat details:', err);
      setError('Failed to load flat details.');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextFlatNumber = useCallback(async (fId) => {
    if (!fId || isEditMode) return;
    try {
      const response = await searchFlats({ floorId: fId, limit: 100 });
      const flats = Array.isArray(response) ? response : (response.data || []);
      if (flats.length > 0) {
        const numbers = flats.map(f => Number(f.flatNumber || f.number)).filter(n => !isNaN(n));
        const max = numbers.length > 0 ? Math.max(...numbers) : 0;
        setFormData(prev => ({ ...prev, number: (max + 1).toString() }));
      } else {
        setFormData(prev => ({ ...prev, number: '1' }));
      }
    } catch (err) {
      console.error('Error suggesting flat number:', err);
    }
  }, [isEditMode]);

  useEffect(() => {
    if (formData.projectType) fetchProjects(formData.projectType);
  }, [formData.projectType]);

  useEffect(() => {
    fetchMetadata();
    if (id && !flatDataFromState) {
      fetchFlatById(id);
    }
  }, [id]);

  useEffect(() => {
    if (formData.projectId) fetchBuildings(formData.projectId);
  }, [formData.projectId]);

  useEffect(() => {
    if (formData.buildingId) fetchFloors(formData.buildingId);
  }, [formData.buildingId]);

  useEffect(() => {
    if (formData.floorId) fetchNextFlatNumber(formData.floorId);
  }, [formData.floorId, fetchNextFlatNumber]);

  useEffect(() => {
    if (facingOptions.length > 0 && formData.facing && formData.facing.length > 15) {
      const found = facingOptions.find(o => o.id === formData.facing);
      if (found) {
        setFormData(prev => ({ ...prev, facing: found.label }));
      }
    }
  }, [facingOptions, formData.facing]);

  const fetchProjects = async (pType) => {
    try {
      setLoadingProjects(true);
      const data = await getProjects({ projectType: pType || formData.projectType });
      const projectsList = data || [];
      setProjects(projectsList);

      // Auto-select if only one project exists
      if (projectsList.length === 1 && !formData.projectId) {
        const singleProject = projectsList[0];
        setFormData(prev => ({ ...prev, projectId: singleProject._id || singleProject.id }));
        fetchBuildings(singleProject._id || singleProject.id);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const [flatList, facingBody] = await Promise.all([
        fetch(`${API_BASE}/api/globalMasterDetails?type=flatType`).then(res => res.json()),
        globalMasterService.getFacing()
      ]);

      const flatData = flatList?.data || flatList?.details || [];
      const facingData = facingBody || [];

      setFlatTypeOptions(flatData.map(item => ({
        id: item.details?.flatType || item.name || '',
        label: item.details?.flatType || item.name || ''
      })).filter(o => o.id));

      setFacingOptions(facingData.map(item => ({
        id: item.id || item._id,
        label: item.label || item.value || ''
      })).filter(o => o.id && o.label));
    } catch (err) {
      console.error('Error fetching metadata:', err);
    }
  };

  const fetchBuildings = async (projectId) => {
    try {
      setLoadingBuildings(true);
      const response = await getPlotsByProject(projectId, formData.projectType, formData.projectType);
      const inventories = Array.isArray(response) ? response : (response?.inventories || []);
      const mapped = inventories.map(b => ({ id: b._id || b.id, name: b.buildingName || b.name }));
      setBuildings(mapped);

      // Auto-select if only one building exists
      if (mapped.length === 1 && !formData.buildingId) {
        setFormData(prev => ({ ...prev, buildingId: mapped[0].id }));
        fetchFloors(mapped[0].id);
      }
    } catch (err) {
      console.error('Error fetching buildings:', err);
      setBuildings([]);
    } finally {
      setLoadingBuildings(false);
    }
  };

  const fetchFloors = async (buildingId) => {
    try {
      setLoadingFloors(true);
      const rawFloors = await searchFloorData({ buildingId });
      const mappedFloors = (rawFloors || []).map(f => ({
        id: f._id || f.id,
        name: f.floorNo || f.name || `Floor ${f.floorNo}`
      }));
      setFloors(mappedFloors);

      // Auto-select if only one floor exists
      if (mappedFloors.length === 1 && !formData.floorId) {
        setFormData(prev => ({ ...prev, floorId: mappedFloors[0].id }));
      }
    } catch (err) {
      console.error('Error fetching floors:', err);
      setFloors([]);
    } finally {
      setLoadingFloors(false);
    }
  };

  const calculateCost = (data) => {
    const price = parseFloat(data.price) || 0;
    const area = parseFloat(data.totalSize) || 0;
    const plc = parseFloat(data.plcPercent) || 0;
    const discountPercent = parseFloat(data.companyDiscountPercent) || 0;

    const discountedBase = price * (1 - discountPercent / 100);
    const plcAmount = price * (plc / 100);
    const totalLacs = discountedBase + plcAmount;

    const discountAmountLocal = price * (discountPercent / 100);
    const discountPerSqFtLocal = (area > 0) ? (discountAmountLocal * 100000 / area) : 0;

    return {
      costLacs: totalLacs > 0 ? totalLacs.toFixed(2) : '',
      discountAmount: discountAmountLocal.toFixed(2),
      discountPerSqFt: discountPerSqFtLocal.toFixed(2)
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updatedData = { ...formData, [name]: value };

    if (name === 'price' || name === 'plcPercent' || name === 'companyDiscountPercent' || name === 'totalSize') {
      const results = calculateCost(updatedData);
      updatedData = { ...updatedData, ...results };
    }

    setFormData(updatedData);
  };

  const handleProjectChange = (event, newValue) => {
    const projectId = newValue?._id || '';
    setFormData((prev) => ({
      ...prev,
      projectId,
      buildingId: '',
      floorId: '',
    }));
    if (projectId) {
      fetchBuildings(projectId);
    } else {
      setBuildings([]);
      setFloors([]);
    }
  };

  const handleBuildingChange = (event) => {
    const buildingId = event.target.value;
    setFormData((prev) => ({ ...prev, buildingId, floorId: '' }));
    if (buildingId) {
      fetchFloors(buildingId);
    } else {
      setFloors([]);
    }
  };

  const handleSubmit = async () => {
    const missing = [];
    if (!formData.projectId) missing.push('Project');
    if (!formData.buildingId) missing.push('Building');
    if (!formData.floorId) missing.push('Floor');
    if (!formData.number) missing.push('Flat Number');
    if (!formData.status) missing.push('Status');

    if (missing.length > 0) {
      setError(`Please fill in the following required fields: ${missing.join(', ')}`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const payload = {
        projectId: formData.projectId,
        buildingId: formData.buildingId,
        floorId: formData.floorId,
        flatNumber: Number(formData.number) || formData.number,
        flatType: formData.type,
        facing: formData.facing,
        inventoryType: formData.inventoryType || 'Apartment',
        status: (formData.status || '').toLowerCase(),
        remark: formData.remark,

        totalSize: parseFloat(formData.totalSize) || 0,
        price: (parseFloat(formData.price) || 0) * 100000,
        plcPercent: parseFloat(formData.plcPercent) || 0,
        companyDiscountPercent: parseFloat(formData.companyDiscountPercent) || 0,
        discountPerSqFt: parseFloat(formData.discountPerSqFt) || 0,
        discountAmount: parseFloat(formData.discountAmount) || 0,
        guidelineValue: parseFloat(formData.guidelineValue) || 0,
        cost: (parseFloat(formData.costLacs) || 0) * 100000,
      };

      if (isEditMode) {
        await updateFlat(formData.id, payload);
      } else {
        await createFlat(payload);
      }

      showSnackbar({
        type: 'success',
        message: isEditMode ? 'Flat updated successfully!' : 'Flat added successfully!'
      });
      history.push('/app/project/apartment/flats', {
        building: { id: formData.buildingId },
        floor: { id: formData.floorId }
      });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => history.goBack()} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a203e', mb: 0.5 }}>
              {isEditMode ? 'Edit Flat' : 'Add New Flat'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditMode ? `Updating unit ${formData.number}` : 'Define a new residential or commercial unit'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        {/* Hierarchy Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ProjectIcon color="primary" /> Hierarchy Assignment
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Property Type</InputLabel>
              <Select
                name="projectType"
                value={formData.projectType}
                onChange={(e) => {
                  handleInputChange(e);
                  setFormData(prev => ({ ...prev, projectId: '', buildingId: '', floorId: '' }));
                  setProjects([]);
                  setBuildings([]);
                  setFloors([]);
                  fetchProjects();
                }}
                disabled={isEditMode || isPreSelectedProject}
                label="Property Type"
              >
                <MenuItem value="Apartment">Apartment</MenuItem>
                <MenuItem value="Commercial">Commercial</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={projects}
              getOptionLabel={(option) => option.projectName || option.name || ''}
              value={projects.find(p => p._id === formData.projectId) || null}
              onChange={handleProjectChange}
              disabled={isEditMode || isPreSelectedProject}
              loading={loadingProjects}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingProjects ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth required>
              <InputLabel>Building</InputLabel>
              <Select
                value={formData.buildingId}
                onChange={handleBuildingChange}
                label="Building"
                disabled={isEditMode || isPreSelectedBuilding || !formData.projectId || loadingBuildings}
              >
                {buildings.map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth required>
              <InputLabel>Floor</InputLabel>
              <Select
                name="floorId"
                value={formData.floorId}
                onChange={handleInputChange}
                label="Floor"
                disabled={(!formData.buildingId && !isEditMode) || loadingFloors}
              >
                {floors.map((f) => (
                  <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Flat Details Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <FlatIcon color="primary" /> Flat Information
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Flat Number / Unit ID"
              name="number"
              value={formData.number}
              onChange={handleInputChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Flat Type</InputLabel>
              <Select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                label="Flat Type"
              >
                {flatTypeOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Facing</InputLabel>
              <Select
                name="facing"
                value={formData.facing}
                onChange={handleInputChange}
                label="Facing"
              >
                {facingOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.label}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                label="Status"
              >
                <MenuItem value="Available">Available</MenuItem>
                <MenuItem value="Booked">Booked</MenuItem>
                <MenuItem value="underConstruction">Under Construction</MenuItem>
                <MenuItem value="Hold">Hold</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Financial Details Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <RupeeIcon color="primary" /> Financial Details
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Area (SqFt)"
              name="totalSize"
              type="number"
              value={formData.totalSize}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Base Price (Lacs)"
              name="price"
              type="number"
              value={formData.price}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="PLC (%)"
              name="plcPercent"
              type="number"
              value={formData.plcPercent}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Discount (%)"
              name="companyDiscountPercent"
              type="number"
              value={formData.companyDiscountPercent}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Discount / SqFt"
              name="discountPerSqFt"
              value={formData.discountPerSqFt}
              disabled
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Discount Amount"
              name="discountAmount"
              value={formData.discountAmount}
              disabled
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                endAdornment: <Typography variant="caption" color="text.secondary">Lacs</Typography>
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Total Cost (Lacs)"
              name="costLacs"
              value={formData.costLacs}
              disabled
              sx={{ '& .MuiInputBase-root': { fontWeight: 700, color: 'primary.main' } }}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Circle Rate"
              name="guidelineValue"
              type="number"
              value={formData.guidelineValue}
              onChange={handleInputChange}
            />
          </Grid>
        </Grid>

        {/* Remarks Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Additional Information
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="remark"
              label="Remarks / Notes"
              value={formData.remark}
              onChange={handleInputChange}
              multiline
              rows={4}
              placeholder="Notes about specific unit features..."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Usage Type</InputLabel>
              <Select
                name="inventoryType"
                value={formData.inventoryType}
                onChange={handleInputChange}
                label="Usage Type"
              >
                <MenuItem value="Apartment">Residential</MenuItem>
                <MenuItem value="Commercial">Commercial</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ mt: 5, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => history.goBack()}
            sx={{ px: 4, py: 1.5, borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              px: 6,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 8px 16px rgba(55, 125, 255, 0.24)',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {loading ? 'Saving...' : isEditMode ? 'Update Flat' : 'Save Flat'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default FlatForm;
